import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useRecentlyViewedItemsContext } from './RecentlyViewedItemsContext';

import { ItemType } from '../recentHistoryTypes';
import { useRecentHistoryList_user$key } from './__generated__/useRecentHistoryList_user.graphql';

const userFragment = graphql`
    fragment useRecentHistoryList_user on User
    @argumentDefinitions(
        excludeItemPks: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 0 }
    ) {
        recentHistoryItems(excludeItemPks: $excludeItemPks, first: $count) {
            edges {
                node {
                    contemporaryTrackingString
                    firstPhotoWebPath(size: small)
                    pdpURL
                    title
                    browseUrl
                    categoryCode
                    serviceId
                    categoryPath
                    seller {
                        serviceId
                    }
                }
            }
        }
    }
`;

type HookState = {
    items: ItemType[] | null;
    hasFetchedItems: boolean | null;
};

export const useRecentHistoryList = ({
    user: userRef,
}: {
    user: useRecentHistoryList_user$key | null;
}): HookState => {
    const user = useFragment(userFragment, userRef);
    const { localStorageItems } = useRecentlyViewedItemsContext();

    const formattedItems = useMemo(() => {
        const fetchedItems = user?.recentHistoryItems?.edges || [];
        return fetchedItems.reduce<ItemType[]>((acc, edge) => {
            const item = edge?.node;
            if (
                !item ||
                !item.title ||
                !item.pdpURL ||
                !item.firstPhotoWebPath ||
                !item.serviceId
            ) {
                return acc;
            }

            return acc.concat({
                category: item.categoryPath,
                contemporaryTrackingString: item.contemporaryTrackingString,
                dealerPk: item?.seller?.serviceId,
                itemBrowseUrl: item.browseUrl,
                itemCategoryCode: item.categoryCode,
                itemId: item.serviceId,
                itemImageUrl: item.firstPhotoWebPath,
                itemPdpUrl: item.pdpURL,
                itemTitle: item.title,
            });
        }, []);
    }, [user]);

    return useMemo(
        () => ({
            items: [...localStorageItems, ...formattedItems],
            hasFetchedItems: !!formattedItems.length,
        }),
        [localStorageItems, formattedItems]
    );
};
